import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const UPDATE_PROFILE_AND_LISTING_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_AND_LISTING_REQUEST';
export const UPDATE_PROFILE_AND_LISTING_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_AND_LISTING_SUCCESS';
export const UPDATE_PROFILE_AND_LISTING_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_AND_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,

  updateProfileAndListingInProgress: false,
  updateProfileAndListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case UPDATE_PROFILE_AND_LISTING_REQUEST:
      return {
        ...state,
        updateProfileAndListingInProgress: true,
        updateProfileAndListingError: null,
      };
    case UPDATE_PROFILE_AND_LISTING_SUCCESS:
      return {
        ...state,
        image: null,
        updateProfileAndListingInProgress: false,
      };
    case UPDATE_PROFILE_AND_LISTING_ERROR:
      return {
        ...state,
        image: null,
        updateProfileAndListingInProgress: false,
        updateProfileAndListingError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const updateProfileAndListingRequest = params => ({
  type: UPDATE_PROFILE_AND_LISTING_REQUEST,
  payload: { params },
});
export const updateProfileAndListingSuccess = result => ({
  type: UPDATE_PROFILE_AND_LISTING_SUCCESS,
  payload: result.data,
});
export const updateProfileAndListingError = error => ({
  type: UPDATE_PROFILE_AND_LISTING_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x', 'variants.scaled-large'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
        return uploadedImage
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};
export const updateProfileAndListing = actionPayload => {
  return async (dispatch, getState, sdk) => {
    dispatch(updateProfileAndListingRequest());

    try {
      const { profileData, listingData, authorListingId, images } = actionPayload || {};
      const queryParams = {
        expand: true,
        include: ['profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x', 'variants.scaled-large'],
      };

      // 1. Upload image if it exists
      let uploadedImageId = null;

      if (images?.attributes?.variants?.['scaled-large']?.url) {
        const imageUrl = images.attributes.variants['scaled-large'].url;
        const imageFile = await fetch(imageUrl)
          .then(res => res.blob())
          .then(blob => new File([blob], 'listing-image.jpg', { type: blob.type }));

        const uploadResponse = await sdk.images.upload(
          { image: imageFile },
          {
            expand: true,
            'fields.image': ['variants.scaled-large', 'variants.scaled-large2x'],
          }
        );
        uploadedImageId = uploadResponse?.data?.data?.id;
      }

      // 2. Update profile
      const profileResponse = await sdk.currentUser.updateProfile(profileData, queryParams);
      dispatch(updateProfileAndListingSuccess(profileResponse));

      const entities = denormalisedResponseEntities(profileResponse);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));

      // 3. Update listing with image ID if uploaded
      const listingResponse = await sdk.ownListings.update({
        id: authorListingId,
        ...listingData,
        images: uploadedImageId ? [uploadedImageId] : []
      });
      return listingResponse;
    } catch (e) {
      dispatch(updateProfileAndListingError(storableError(e)));
    }
  };
};

